import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import {
  ChatContextValue,
  ChatConversation,
  ChatConversationRequestOptions,
  ChatMessage,
  ChatSnapshot,
  CreateConversationPayload,
  SendMessagePayload,
} from "../types";
import { createDefaultSnapshot } from "../utils/snapshot";
import { mergeConversation } from "../utils/snapshotMutations";
import {
  createConversation as apiCreateConversation,
  fetchConversation as apiFetchConversation,
  fetchSnapshot as apiFetchSnapshot,
  markConversationAsRead as apiMarkConversationAsRead,
} from "../services/chatApi";
import { CHAT_FEATURE_ENABLED } from "../services/config";
import { chatSocketService } from "../services/socket";
import { chatTokenManager } from "../services/token";
import { normalizeConversation, normalizeMessage } from "../utils/normalizers";

type State = {
  snapshot: ChatSnapshot;
  activeConversationId: string | null;
  loadingSnapshot: boolean;
  error: string | null;
};

type Action =
  | { type: "setSnapshot"; snapshot: ChatSnapshot }
  | { type: "setLoading"; loading: boolean }
  | { type: "setError"; error: string | null }
  | { type: "setActiveConversation"; conversationId: string | null }
  | { type: "mergeConversation"; conversation: ChatConversation }
  | { type: "appendMessage"; conversationId: string; message: ChatMessage; currentUserId: string | null };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "setSnapshot":
      return { ...state, snapshot: action.snapshot };
    case "setLoading":
      return { ...state, loadingSnapshot: action.loading };
    case "setError":
      return { ...state, error: action.error };
    case "setActiveConversation":
      return { ...state, activeConversationId: action.conversationId };
    case "mergeConversation":
      return { ...state, snapshot: mergeConversation(state.snapshot, action.conversation) };
    case "appendMessage": {
      const conversation = state.snapshot.roster[action.conversationId];
      if (!conversation) {
        return state;
      }

      const nextMessages = [...conversation.messages, action.message];
      const nextConversation: ChatConversation = {
        ...conversation,
        messages: nextMessages,
        lastMessage: action.message.text,
        lastMessageId: action.message.id,
        time: action.message.time ?? action.message.sentAt ?? new Date().toISOString(),
      };

      const isOwnMessage = action.currentUserId && action.message.senderId === action.currentUserId;
      if (isOwnMessage || state.activeConversationId === conversation.id) {
        nextConversation.unreadCount = 0;
        if (nextConversation.membership) {
          nextConversation.membership = {
            ...nextConversation.membership,
            unreadCount: 0,
            lastReadMessageId: action.message.id,
            lastReadAt: new Date().toISOString(),
          };
        }
      } else {
        const currentUnread = typeof nextConversation.unreadCount === "number" ? nextConversation.unreadCount : 0;
        nextConversation.unreadCount = currentUnread + 1;
        if (nextConversation.membership) {
          nextConversation.membership = {
            ...nextConversation.membership,
            unreadCount: nextConversation.unreadCount,
          };
        }
      }

      return { ...state, snapshot: mergeConversation(state.snapshot, nextConversation) };
    }
    default:
      return state;
  }
}

const INITIAL_STATE: State = {
  snapshot: createDefaultSnapshot(),
  activeConversationId: null,
  loadingSnapshot: false,
  error: null,
};

type Options = {
  currentUserId: string | null;
};

export function useChatController({ currentUserId }: Options): ChatContextValue {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const activeConversationUnsubscribeRef = useRef<(() => void) | null>(null);
  const disabledErrorMessage = "Telemedicine chat is disabled";

  const refreshSnapshot = useCallback(async () => {
    dispatch({ type: "setLoading", loading: true });
    try {
      if (!CHAT_FEATURE_ENABLED) {
        dispatch({ type: "setError", error: disabledErrorMessage });
        return;
      }

      const token = await chatTokenManager.getToken();
      const snapshot = await apiFetchSnapshot(token);
      dispatch({ type: "setSnapshot", snapshot });
      dispatch({ type: "setError", error: null });
    } catch (error) {
      console.error("Failed to load chat snapshot", error);
      dispatch({ type: "setError", error: error instanceof Error ? error.message : "Unable to load chat" });
    } finally {
      dispatch({ type: "setLoading", loading: false });
    }
  }, []);

  const handleIncomingConversation = useCallback(
    (payload: unknown) => {
      try {
        const conversation = normalizeConversation(payload);
        dispatch({ type: "mergeConversation", conversation });
      } catch (error) {
        console.warn("Ignoring invalid conversation payload", error);
      }
    },
    [dispatch]
  );

  const handleIncomingEvent = useCallback(
    (payload: any) => {
      if (!payload) return;

      if (payload.conversation) {
        handleIncomingConversation(payload.conversation);
        return;
      }

      if (payload.message) {
        try {
          const message = normalizeMessage(payload.message);
          dispatch({
            type: "appendMessage",
            conversationId: message.conversationId,
            message,
            currentUserId,
          });
        } catch (error) {
          console.warn("Ignoring invalid chat message", error);
        }
        return;
      }

      if (payload.id && payload.members && payload.messages) {
        handleIncomingConversation(payload);
      }
    },
    [currentUserId, handleIncomingConversation]
  );

  useEffect(() => {
    refreshSnapshot();
  }, [refreshSnapshot]);

  useEffect(() => {
    if (!CHAT_FEATURE_ENABLED) {
      return;
    }

    let unsubscribe: (() => void) | null = null;
    (async () => {
      try {
        unsubscribe = await chatSocketService.subscribeToRoster(handleIncomingEvent);
      } catch (error) {
        console.error("Failed to subscribe to chat roster", error);
        dispatch({ type: "setError", error: error instanceof Error ? error.message : "Unable to connect to chat" });
      }
    })();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [handleIncomingEvent]);

  useEffect(() => {
    return () => {
      if (activeConversationUnsubscribeRef.current) {
        activeConversationUnsubscribeRef.current();
        activeConversationUnsubscribeRef.current = null;
      }
    };
  }, []);

  const setActiveConversation = useCallback(
    async (conversationId: string | null) => {
      dispatch({ type: "setActiveConversation", conversationId });

      if (activeConversationUnsubscribeRef.current) {
        activeConversationUnsubscribeRef.current();
        activeConversationUnsubscribeRef.current = null;
      }

      if (!conversationId) {
        return;
      }

      if (!CHAT_FEATURE_ENABLED) {
        dispatch({ type: "setError", error: disabledErrorMessage });
        return;
      }

      try {
        activeConversationUnsubscribeRef.current = await chatSocketService.subscribeToConversation(
          conversationId,
          handleIncomingEvent
        );
      } catch (error) {
        console.error("Failed to subscribe to conversation", error);
        dispatch({ type: "setError", error: error instanceof Error ? error.message : "Unable to join conversation" });
      }
    },
    [handleIncomingEvent]
  );

  const loadConversation = useCallback(
    async (conversationId: string, options?: ChatConversationRequestOptions) => {
      try {
        if (!CHAT_FEATURE_ENABLED) {
          throw new Error(disabledErrorMessage);
        }

        const token = await chatTokenManager.getToken();
        const conversation = await apiFetchConversation(token, conversationId, options);
        if (conversation) {
          dispatch({ type: "mergeConversation", conversation });
        }
        return conversation;
      } catch (error) {
        console.error("Failed to fetch conversation", error);
        dispatch({ type: "setError", error: error instanceof Error ? error.message : "Unable to load conversation" });
        return null;
      }
    },
    []
  );

  const sendMessage = useCallback(
    async ({ conversationId, text, metadata, senderId, senderName }: SendMessagePayload) => {
      if (!conversationId || !text) {
        return;
      }

      try {
        if (!CHAT_FEATURE_ENABLED) {
          throw new Error(disabledErrorMessage);
        }

        await chatSocketService.sendToConversation(conversationId, {
          conversationId,
          text,
          metadata,
          senderId: senderId ?? undefined,
          senderName: senderName ?? undefined,
        });
      } catch (error) {
        console.error("Failed to send chat message", error);
        const errorMessage = error instanceof Error ? error.message : "Unable to send message";
        dispatch({ type: "setError", error: errorMessage });
        throw (error instanceof Error ? error : new Error(errorMessage));
      }
    },
    []
  );

  const createConversation = useCallback(
    async (payload: CreateConversationPayload) => {
      try {
        if (!CHAT_FEATURE_ENABLED) {
          throw new Error(disabledErrorMessage);
        }

        const token = await chatTokenManager.getToken();
        const conversation = await apiCreateConversation(token, payload);
        dispatch({ type: "mergeConversation", conversation });
        return conversation;
      } catch (error) {
        console.error("Failed to create conversation", error);
        dispatch({ type: "setError", error: error instanceof Error ? error.message : "Unable to create conversation" });
        return null;
      }
    },
    []
  );

  const markConversationAsRead = useCallback(
    async (conversationId: string) => {
      if (!conversationId || !currentUserId) return;
      try {
        if (!CHAT_FEATURE_ENABLED) {
          throw new Error(disabledErrorMessage);
        }

        const token = await chatTokenManager.getToken();
        const conversation = await apiMarkConversationAsRead(token, conversationId, currentUserId);
        dispatch({ type: "mergeConversation", conversation });
        await chatSocketService.sendReadReceipt(conversationId, currentUserId);
      } catch (error) {
        console.error("Failed to mark conversation as read", error);
        dispatch({ type: "setError", error: error instanceof Error ? error.message : "Unable to mark conversation as read" });
      }
    },
    [currentUserId]
  );

  const activeConversation = useMemo(() => {
    if (!state.activeConversationId) return null;
    return state.snapshot.roster[state.activeConversationId] ?? null;
  }, [state.activeConversationId, state.snapshot.roster]);

  return {
    snapshot: state.snapshot,
    activeConversationId: state.activeConversationId,
    activeConversation,
    loadingSnapshot: state.loadingSnapshot,
    error: state.error,
    setActiveConversation,
    loadConversation,
    sendMessage,
    createConversation,
    markConversationAsRead,
    refreshSnapshot,
  };
}

